import { BackendError, Service } from '@genial/services';
import type { EquityEnumsSortPublicOfferingSortByEnum } from '@genial/services/dist/models';
import {
  MetaFilterResponseEquityFiltersPublicOfferingsFilters,
  MetaResponse,
} from '@genial/services/dist/models';
import { selectorFamily } from 'recoil';

import { blankMetaResponse } from '../../../utils/blankMetaResponse';
import { metaFilterToOptions } from '../../../utils/metaFilterToOptions';
import { transformSortItem } from '../../../utils/transformSortItem';
import { advisorIdToApisSelector, partnerIdToApisSelector } from '../../master/selector';
import { publicOffersProductsLoadedState } from '../../product/atom';
import {
  normalizePublicOfferAvailableProductsDetails,
  normalizePublicOffersSearch,
} from '../../product/normalizer';
import {
  DynamicCustomFiltersProps,
  PublicOfferDetailsNormalized,
  PublicOfferEntityNormalized,
} from '../../product/types';
import { ORDER_VALUE_TO_SORT_BY, screenName } from '../constants';
import { DynamicProductQueryParams } from '../types';

export const publicOffersAvailableProductsQuery = selectorFamily<
  { data: PublicOfferEntityNormalized[]; meta: MetaResponse },
  DynamicProductQueryParams
>({
  key: 'AvailableProducts/publicOffersAvailableProductsQuery',
  get: ({ filtersToApply, searchTerm, page, orderBy }) => {
    return async ({ get }) => {
      try {
        const advisorSelected = get(advisorIdToApisSelector);
        const partnerSelected = get(partnerIdToApisSelector);
        const sortItems = transformSortItem(orderBy);
        const sortBy =
          sortItems?.sort != null ? ORDER_VALUE_TO_SORT_BY[sortItems.sort] ?? undefined : undefined;

        const { data, meta } = await Service.Equities.V1.getPublicOfferingsSearch({
          ...filtersToApply,
          ...(searchTerm ? { searchTerm } : {}),
          ...(sortBy != null ? { sortBy: sortBy as EquityEnumsSortPublicOfferingSortByEnum } : {}),
          ...(sortItems?.sortDirection !== undefined
            ? { sortAscending: sortItems.sortDirection === 'asc' }
            : {}),
          page,
          limit: 10,
          screenName,
          advisorId: advisorSelected,
          partnerId: partnerSelected,
        });

        const normalizedData = normalizePublicOffersSearch(data || []);

        return { data: normalizedData, meta };
      } catch (error) {
        const backendError = error as BackendError;

        if (backendError.statusCode === 404) {
          return { data: [], meta: blankMetaResponse() };
        }

        throw error;
      }
    };
  },
});

export const publicOffersAvailableProductsFiltersQuery = selectorFamily<
  DynamicCustomFiltersProps,
  undefined
>({
  key: 'AvailableProducts/publicOffersAvailableProductsFiltersQuery',
  get: () => {
    return async ({ get }) => {
      const loadedPublicOffers = get(publicOffersProductsLoadedState);

      if (!loadedPublicOffers.data.length) {
        return { filters: [] };
      }

      const meta = loadedPublicOffers.meta as MetaFilterResponseEquityFiltersPublicOfferingsFilters;
      const filter = meta?.filter;

      const offerTypeOptions = metaFilterToOptions(filter?.categoryTypes);
      const suitabilityOptions = metaFilterToOptions(filter?.riskLevelTypes);
      const limiterOptions = metaFilterToOptions(filter?.rateLimiterTypes);

      return {
        filters: [
          { fieldName: 'publicOfferingType', label: 'Tipo da oferta', options: offerTypeOptions },
          {
            fieldName: 'riskClassName',
            label: 'Perfil de risco',
            options: suitabilityOptions,
          },
          {
            fieldName: 'pricingMethod',
            label: 'Limitador',
            options: limiterOptions,
          },
        ],
      };
    };
  },
});

export const publicOffersAvailableProductsDetailsQuery = selectorFamily<
  PublicOfferDetailsNormalized | null | undefined,
  { publicOfferId?: number }
>({
  key: 'AvailableProducts/publicOffersAvailableProductsDetailsQuery',
  get: ({ publicOfferId }) => {
    return async () => {
      if (!publicOfferId) {
        return;
      }

      const apiData = await Service.Equities.V1.getPublicOfferingsOfferingOptionsOfferId({
        publicOfferId,
        screenName,
      });

      const normalizedData = normalizePublicOfferAvailableProductsDetails(apiData);

      return normalizedData;
    };
  },
});
